import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { siteConfig } from '@/mocks/siteConfig';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9somgl8o1vn02ekuj60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed?.message as string)
          || responseText
          || '提交失败，请重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.siteName}`,
    description: siteConfig.siteDescription,
    url: `${siteConfig.siteUrl}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: siteConfig.siteName,
      description: siteConfig.siteDescription,
      url: siteConfig.siteUrl,
      foundingDate: '2018',
      email: siteConfig.email,
      telephone: siteConfig.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: siteConfig.address,
      },
    },
  };

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteConfig.siteName}`}
        description={`了解${siteConfig.siteName}的品牌故事、核心理念与团队背景。${siteConfig.siteDescription}`}
        keywords="关于秘密教学,秘密教学团队,学习方法论,认知教育,深度学习平台"
        canonicalUrl={`${siteConfig.siteUrl}/about`}
        jsonLd={jsonLd}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[320px] md:h-[420px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20library%20or%20study%20space%20with%20soft%20golden%20light%2C%20bookshelves%20and%20geometric%20shapes%2C%20cream%20and%20amber%20tones%2C%20intellectual%20atmosphere%2C%20clean%20modern%20design%2C%20inspiring%20and%20contemplative&width=1800&height=900&seq=about-hero-01&orientation=landscape"
            alt="关于秘密教学"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45" />
          <div className="relative z-10 text-center px-4">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-lg mx-auto">
              了解秘密教学背后的故事与使命
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-6">
              品牌故事
            </h2>
            <div className="flex flex-col gap-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                秘密教学诞生于2018年，由一群对认知科学和教育创新充满热情的学者和实践者共同创立。我们的创始人团队来自认知心理学、教育技术、人工智能和传统师徒传承研究等多个交叉领域。
              </p>
              <p>
                创立之初，我们就观察到一个普遍现象：最深刻、最有价值的学习往往发生在正式教育体系之外。无论是手工艺大师的言传身教、投资高手的内部研讨、还是顶尖科学家的实验室讨论——这些真正改变认知的学习经历，都具有某些共通的结构和原则。
              </p>
              <p>
                我们花了数年时间，系统性地研究、提炼和验证这些原则，最终形成了"秘密教学"这一方法论框架。我们的使命是将这些隐藏在高墙之后的高阶认知方法，以系统化、可学习的方式呈现给所有渴望突破学习瓶颈的求知者。
              </p>
              <p>
                今天，秘密教学已经发展成为一个拥有超过15,000名月度活跃读者的知识平台，我们的内容被翻译成多种语言，影响了来自全球50多个国家和地区的深度学习者。但我们深知，这只是开始——隐藏知识的挖掘和系统化，是一条永无止境的探索之路。
              </p>
            </div>
          </div>
        </section>

        {/* Reputation / Stats */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-3">
              声誉与影响力
            </h2>
            <p className="text-sm text-foreground-600 mb-10">
              秘密教学在过去几年中获得的多项认可与成就
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-award-line', title: '2024年度最佳教育内容平台', desc: '中国教育创新峰会评选' },
                { icon: 'ri-star-line', title: '读者满意度 4.9/5.0', desc: '来自超过2,000份读者调研反馈' },
                { icon: 'ri-global-line', title: '覆盖50+国家和地区', desc: '多语言内容，全球影响力持续增长' },
                { icon: 'ri-quote-text', title: '被30+知名媒体引用', desc: '包括学术期刊和主流教育媒体' },
                { icon: 'ri-group-line', title: '15,000+ 月度活跃读者', desc: '持续增长的深度学习社区' },
                { icon: 'ri-book-open-line', title: '40+ 原创深度文章', desc: '每篇平均阅读时长超过10分钟' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 flex-shrink-0">
                    <i className={`${item.icon} text-lg`} />
                  </div>
                  <div>
                    <p className="font-heading text-sm font-semibold text-foreground-900 mb-1">{item.title}</p>
                    <p className="text-xs text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                  如果你对秘密教学有任何疑问、建议或合作意向，欢迎通过以下方式或表单与我们联系。
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{siteConfig.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line text-lg" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电话</p>
                      <p className="text-sm font-medium text-foreground-900">{siteConfig.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm font-medium text-foreground-900 leading-relaxed">{siteConfig.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-100 rounded-lg p-5 border border-background-200/70"
                >
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                    发送消息
                  </h3>

                  <div className="flex flex-col gap-3">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-md text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-md text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-md text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">
                        消息内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-md text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的消息（限500字）"
                      />
                    </div>

                    <input type="text" name="phone_alt" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                    <button
                      type="submit"
                      className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer"
                    >
                      发送消息
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center">
                        消息已成功发送，我们会尽快回复您！
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-600 text-center">
                        {formError}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}